import { Suspense, lazy, useContext, useEffect, useMemo } from "react";
import { twMerge } from "tailwind-merge";
import { useBoolean } from "usehooks-ts";

import { switchOrg } from "api/api-server-actions";
import { serverGetPageAssessmentStatuses } from "api/api-server-data";
import { getCourseUserData } from "api/api-worksheets";
import { LoadingScreen } from "components/LoadingScreen";
import { MobileRoutingOverlay } from "components/MobileRoutingOverlay";
import {
  selectCourseContentSummaryContext,
  selectCourseMetadataContext,
} from "components/ai/context/course-context";
import { selectCurrentCourseOutcomeContext } from "components/ai/context/outcome-context";
import { CONTENT_TYPE_EXPLANATIONS } from "components/ai/context/view-context";
import { useAIContext } from "components/ai/hooks/use-ai-context";
import { useAITool } from "components/ai/hooks/use-ai-tool";
import { withActiveAv } from "components/av/AvSubsystemLoader";
import { MainFooter } from "components/footer/MainFooter";
import { MainHeader } from "components/header/MainHeader";
import { MobileMainHeader } from "components/header/MobileMainHeader";
import { isProjectorView } from "components/materials/presentation/projector/projector-messaging";
import { t } from "i18n/i18n";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { BypassReleaseWarningContext } from "providers/BypassReleaseWarningProvider";
import { KeyboardShortcutsController } from "providers/KeyboardShortcutsController";
import { WebsocketContext } from "providers/WebsocketProvider";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  selectCourseById,
  selectCurrentClassUser,
  selectCurrentCourseId,
  selectCurrentCourseRole,
  selectCurrentUserId,
  selectIsClassSessionVirtual,
  selectIsRecordingAgent,
  selectIsSidebarOpen,
  selectOrgIdForCourseId,
} from "store/selectors";
import {
  selectClassStateLastUpdatedAt,
  selectIsObservingVirtualClass,
  selectStableIsMainAvTab,
} from "store/selectors/pubsub-virtual-class-state";
import { attendanceStateActions } from "store/slices/attendance";
import { computedStateActions } from "store/slices/computed";
import { PubsubVirtualClassStateActions } from "store/slices/pubsub-virtual-class-state";
import { trackUserProperty } from "utils/amplitude";
import { getOrgIdFromCookie, setLastCourseIdCookie } from "utils/auth";
import { useAv, useAvPubsubMessaging } from "utils/av";
import { isTestEnv } from "utils/env";
import { logInfoWithDebugFlag, rollbarAndLogError, rollbarAndLogWarning } from "utils/logger";
import { Outlet, useParams } from "utils/routing";
import { isAVEnabled } from "utils/waffle-flags";
import { ErrorBoundary } from "worksheets/views/ErrorBoundary";

const LazyParticipantBar = withActiveAv(
  () => import("components/av/ParticipantBar"),
  t("av.loading_users"),
  true,
);

const AvContextProvider = lazy(() => import("components/av/AvProvider"));

export default function Layout() {
  const isMobile = !useIsMdOrLarger();
  const isSidebarOpen = useAppSelector(selectIsSidebarOpen);

  // Ensure this component refreshes when we navigate between the course routes
  const { courseId } = useParams();
  // It's important we lazy-load any entry to the AV subsystem, as it uses many heavy dependencies
  // and is only rarely enabled.
  const orgIdForCourseId = useAppSelector((s) => selectOrgIdForCourseId(s, courseId));
  const orgIdFromCookie = getOrgIdFromCookie();
  const userId = useAppSelector(selectCurrentUserId);
  const { canAuthorCourse } = useCapabilities();
  const dispatch = useAppDispatch();
  const currentCourseId = useAppSelector(selectCurrentCourseId);

  const classUser = useAppSelector(selectCurrentClassUser);
  const isProjectorPath = isProjectorView();

  const { websocketStatus } = useContext(WebsocketContext);

  const { sendPubsubVirtualClassStateMessage } = useAvPubsubMessaging();

  const stableIsMainAvTab = useAppSelector(selectStableIsMainAvTab);
  const hasVirtualClassPubsubUpdate = useAppSelector(selectClassStateLastUpdatedAt) !== null;

  const course = useAppSelector((state) => selectCourseById(state, courseId!));
  const courseMetadataContext = useAppSelector((state) =>
    selectCourseMetadataContext(state, course),
  );
  const courseContentSummaryContext = useAppSelector((state) =>
    selectCourseContentSummaryContext(state),
  ).context;
  const courseOutcomeContext = useAppSelector((state) => selectCurrentCourseOutcomeContext(state));
  useAIContext("content_type_explanations", CONTENT_TYPE_EXPLANATIONS, "system");
  useAIContext("current_course_metadata", courseMetadataContext, "system");
  useAIContext("current_course_content", courseContentSummaryContext, "system");
  useAIContext("current_course_outcomes", courseOutcomeContext, "system");
  useAITool("read_page");

  const courseRole = useAppSelector(selectCurrentCourseRole);
  useEffect(() => {
    if (courseRole) {
      trackUserProperty("courseRole", courseRole);
    }
  }, [courseRole]);

  const isClassSessionVirtual = useAppSelector(selectIsClassSessionVirtual);

  const isRecordingAgent = useAppSelector(selectIsRecordingAgent);
  useEffect(() => {
    if (classUser?.id && isRecordingAgent) {
      storeApi.class_users.partial_update({ id: classUser?.id, is_in_class_session: true });
    }
  }, [isRecordingAgent, classUser?.id]);

  // Only used to determine if we should be in dark mode or not.
  const { isInVirtualClass: isDarkMode } = useAv();

  // We consider for this component that a user is in a virtual class if the virtual class exists
  // and the user is either participating or performing an AV check to get in.
  const isUserInVirtualClass =
    !isProjectorPath &&
    isClassSessionVirtual &&
    (classUser?.is_in_class_session || classUser?.is_performing_av_check);

  // Virtual class setup: instructor is performing AV check
  const isVirtualClassSetup = useMemo(
    () => canAuthorCourse && classUser?.is_performing_av_check,
    [canAuthorCourse, classUser?.is_performing_av_check],
  );

  // We should consider starting the AV subsystem if the user is in a virtual class session or setup.
  const isVirtualSessionOrSetup = isAVEnabled() && (isUserInVirtualClass || isVirtualClassSetup);

  // We should request the AV state if the websocket is connected to make sure the join message is sent.
  const shouldRequestAvState = websocketStatus === "connected" && isVirtualSessionOrSetup;

  const isObservingClass = useAppSelector(selectIsObservingVirtualClass);

  const shouldStartAv = stableIsMainAvTab || isObservingClass;

  // This useEffect ensures we log the state of shouldStartAv whenever it changes,
  // which is useful for debugging the AV subsystem mounting and unmounting.
  useEffect(() => {
    if (!isTestEnv) {
      logInfoWithDebugFlag(
        "debug_virtual_class",
        "[Layout] shouldStartAv changed:",
        shouldStartAv,
        {
          shouldRequestAvState,
          hasVirtualClassPubsubUpdate,
          stableIsMainAvTab,
          websocketStatus,
          isProjectorPath,
          avEnabled: isAVEnabled(),
          isUserInVirtualClass,
          isVirtualClassSetup,
          classUser: {
            is_in_class_session: classUser?.is_in_class_session,
            is_performing_av_check: classUser?.is_performing_av_check,
          },
        },
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldStartAv]);

  useEffect(() => {
    if (isObservingClass) return;
    if (shouldRequestAvState && !hasVirtualClassPubsubUpdate) {
      logInfoWithDebugFlag(
        "debug_virtual_class",
        "[Layout] Virtual Class. Sending join message, force: false",
      );
      sendPubsubVirtualClassStateMessage({
        action: "join",
        force: isRecordingAgent || false,
      });
    } else if (hasVirtualClassPubsubUpdate && !shouldRequestAvState) {
      if (stableIsMainAvTab) {
        logInfoWithDebugFlag(
          "debug_virtual_class",
          "[Layout] Virtual Class. Sending leave message",
        );
        sendPubsubVirtualClassStateMessage({ action: "leave" });
      }
      logInfoWithDebugFlag(
        "debug_virtual_class",
        "[Layout] Clearing virtual class state not in AV tab nor observing",
      );
      dispatch(PubsubVirtualClassStateActions.clearVirtualClassState());
    }
  }, [
    shouldRequestAvState,
    isObservingClass,
    hasVirtualClassPubsubUpdate,
    stableIsMainAvTab,
    sendPubsubVirtualClassStateMessage,
    dispatch,
    courseId,
    isRecordingAgent,
  ]);

  const needsOrgSwitch = orgIdForCourseId && orgIdForCourseId !== orgIdFromCookie;

  useEffect(() => {
    if (!userId || !courseId) {
      return;
    }
    // If we detect that we need to switch org, we skip the course retrieval
    if (needsOrgSwitch) {
      return;
    }
    storeApi.courses.retrieve(courseId, { skipToast: true }).catch((error) => {
      if ([404, 403, 401].includes(error.status)) {
        rollbarAndLogWarning("Course not found, rerouting to index", error);
        window.location.href = "/";
      } else {
        rollbarAndLogError("Failed to load course", error);
      }
    });
  }, [courseId, userId, needsOrgSwitch]);

  // If we try to load a course URL that doesn't exist, but it _does_ exist in another org
  // that the user has access to, we switch org and reload the page.
  useEffect(() => {
    if (needsOrgSwitch) {
      switchOrg(orgIdForCourseId)
        .then(() => {
          window.location.reload();
        })
        .catch((error) => {
          // We don't show the error the user, because it's too technical. They will just see a 404.
          rollbarAndLogError("Failed to auto-switch org", error);
        });
    }
  }, [needsOrgSwitch, orgIdForCourseId]);

  // Whenever we navigate to a course, we want to remember which course it was, so that accessing
  // the app root will preferably load the same course again.
  useEffect(() => {
    setLastCourseIdCookie(courseId);
  }, [courseId]);

  useEffect(() => {
    const loadCourseData = async () => {
      storeApi.courses.list();
      const courseUsers = await storeApi.course_users.list({ course_id: courseId });
      storeApi.course_codes.list();
      storeApi.terms.list();
      storeApi.class_sessions.list({ course_id: courseId });
      storeApi.topics.list({ course_id: courseId });
      storeApi.pages.list({ course_id: courseId });
      storeApi.page_access_data.list({ course_id: courseId });
      storeApi.recording_sessions.list({ course_id: courseId });
      storeApi.outcomes.list();
      storeApi.course_outcomes.list({ course_id: courseId });
      storeApi.page_outcomes.list({ course_id: courseId });
      const statuses = await serverGetPageAssessmentStatuses({ courseId });
      // Dispatch statuses immediately as each batch completes
      dispatch(computedStateActions.savePageAssessmentStatuses(statuses));

      // Get or create class user if not exists
      const classUsers = await storeApi.class_users.list({ course_id: courseId });
      const currentCourseUserId = courseUsers.find(
        (courseUser) => courseUser.user_id === userId,
      )?.id;

      if (!currentCourseUserId) {
        logInfoWithDebugFlag(
          "debug_virtual_class",
          "Current courseUser not found: not creating class user",
          {
            courseId,
            userId,
          },
        );
        return;
      }

      const currentClassUser = classUsers.find((cu) => cu.course_user_id === currentCourseUserId);
      if (!currentClassUser) {
        logInfoWithDebugFlag("debug_virtual_class", "Creating class user", { courseId, userId });
        await storeApi.class_users.create({
          course_id: courseId,
          course_user_id: currentCourseUserId,
        });
      }
    };

    // Once we have the course and know it exists, we can start loading all related data
    if (courseId && currentCourseId === courseId && !!userId) {
      loadCourseData();
    }
    // eslint-disable-next-line no-object-deps/no-object-deps
  }, [currentCourseId, courseId, dispatch, userId]);

  useEffect(() => {
    const getCourseActivityData = async () => {
      const activityData = await getCourseUserData(courseId);
      dispatch(attendanceStateActions.updateAnalytics(activityData));
    };

    if (userId) {
      getCourseActivityData();
    }
  }, [courseId, dispatch, userId]);

  const { value: showWarning, setTrue: resetWarning, setFalse: bypassWarning } = useBoolean(true);
  const bypassReleaseWarningValue = useMemo(
    () => ({ showWarning, resetWarning, bypassWarning }),
    [showWarning, resetWarning, bypassWarning],
  );

  const layoutContent = (
    <div className={twMerge("flex h-full flex-col", isDarkMode && "bg-black-tint-15")}>
      {!isObservingClass && <KeyboardShortcutsController />}
      {!isProjectorPath && (isMobile ? <MobileMainHeader /> : <MainHeader />)}
      {shouldStartAv && <LazyParticipantBar />}
      <div className="flex h-1 grow" data-sidebar-open={isMobile ? undefined : isSidebarOpen}>
        <div className={twMerge("relative h-full max-w-full grow", isDarkMode && "px-3")}>
          <ErrorBoundary>
            <MobileRoutingOverlay />
            <Outlet />
          </ErrorBoundary>
        </div>
      </div>
      {!isProjectorPath && <MainFooter />}
    </div>
  );

  return (
    <BypassReleaseWarningContext.Provider value={bypassReleaseWarningValue}>
      {shouldStartAv ? (
        <Suspense fallback={<LoadingScreen>{t("av.loading_av_subsystem")}</LoadingScreen>}>
          <AvContextProvider>{layoutContent}</AvContextProvider>
        </Suspense>
      ) : (
        layoutContent
      )}
    </BypassReleaseWarningContext.Provider>
  );
}
